import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import Swal from 'sweetalert2'

import { TApi } from '../../../../../../shared/const'
import http from '../../../../../../shared/http'

import Btn from '../../../../../../UI/Btn'
import { PlainInput } from '../../../../../../UI/PlainField'

import SettingHint from '../SettingHint'

import SupportCategories from './SupportCategories'
import SupporterRequestReminders from './RequestReminders'
import SupporterContactFollowUp from './ContactFollowUp'
import SupporterApprovalReminders from './ApprovalReminders'
import SupporterInvitationReminders from './InvitationReminders'

// Rahmen, Titel und Erklärung kommen aus der gemeinsamen Hülle aller
// Einstellungen; hier liegen nur noch die Merkmale der Schlagwortliste.
import shared from '../style.module.scss'
import styles from './style.module.scss'

/**
 * Pflege der Stammdaten des Unterstützer:innen-Moduls.
 *
 * Anders als die übrigen Einstellungen liegen diese Listen in eigenen Tabellen,
 * weil danach gefiltert wird. Deshalb ein eigener Baustein statt des
 * Options-Formulars — die Oberfläche folgt aber demselben Karten-Aufbau wie
 * jede andere Einstellung.
 *
 * Einträge, die bereits jemand ausgewählt hat, werden nicht gelöscht, sondern
 * deaktiviert — sonst verschwänden sie aus dessen Profil. Die Oberfläche zeigt
 * die Verwendungszahl, damit das nachvollziehbar ist.
 */
/*
 * „Branchen" und „Schultypen" werden bewusst NICHT hier gepflegt.
 *
 * Unterstützer:innen wählen beides aus denselben globalen Listen, aus denen eine
 * Junior Company ihre Branche und eine Schule ihren Typ wählt (Einstellungen
 * weiter oben). Ein zweiter Katalog daneben führt zu abweichenden Werten — und
 * der Reiter „Passende Expert:innen" vergleicht beide Seiten direkt: Bei den
 * Schultypen war das Anforderung #6, bei den Branchen fiel es am 07.08.2026 auf.
 * Die beiden Listen hatten keinen einzigen gemeinsamen Wert, weshalb jede Person
 * mit Zielbranche aus der Passend-Liste jeder Junior Company verschwand.
 *
 * „Schulstufen" gibt es seit Anforderung #57 gar nicht mehr: Unterstützer:innen
 * ordnen sich Schultypen zu, und auf der Schulseite hatte die Stufe nie ein
 * Gegenstück. Damit bleiben hier die Kompetenzen.
 *
 * Und zwar eine einzige Liste für alle drei Sub-Rollen: Eigene Felder für Jury
 * (Veranstaltungsarten) und Trainer:innen (Trainingsinhalte) sind mit
 * Anforderung #55 am 24.08.2026 verworfen worden — die Veranstaltungen
 * unterscheiden sich je Bundesland und gehören deshalb in die Beschreibung.
 *
 * Ein Name darf nur einmal vorkommen, auch über Gruppen hinweg: Profil, Filter
 * und Auswahl arbeiten mit dem Namen und nicht mit der ID, zwei gleiche Namen
 * wären deshalb nicht auseinanderzuhalten (Anforderung #57). Der Server weist
 * das ab und sagt, in welcher Gruppe der Name schon steht. Aufgefallen ist es
 * am Sammeleintrag „Sonstiges", den die freigegebene Liste in jeder Gruppe
 * hatte; Junior Achievement hat ihn am 24.08.2026 fallen gelassen — „Weitere
 * Kompetenzen" im Profil leistet dasselbe und sagt mehr aus.
 */
const LISTS = [
	{
		type: 'competences',
		title: 'Kompetenzen',
		description:
			'Fachgebiete, die Unterstützer:innen in ihrem Profil angeben. Es ist dieselbe Liste für Wirtschaftsexpert:innen, Jurymitglieder und Trainer:innen. Gruppen dienen nur der Übersicht in der Auswahl — jeder Name darf trotzdem nur einmal vorkommen, auch in verschiedenen Gruppen. Zusätzlich können Unterstützer:innen eigene Kompetenzen eintragen, die hier nicht erscheinen.',
		example: 'Beispiel: Gruppe „Recht & Compliance" mit „Vertragswesen", „Grundkenntnisse Wirtschaftsrecht"',
		grouped: true
	}
]

function LookupList({ config }) {
	const { t } = useTranslation()
	const [rows, setRows] = useState([])
	const [isLoading, setLoading] = useState(false)
	const [draft, setDraft] = useState({ name: '', groupName: '' })

	const load = useCallback(() => {
		setLoading(true)
		http.get(TApi.ADMIN_SUPPORTER_LOOKUP_LIST(config.type))
			.then(({ data }) => setRows(data))
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setLoading(false))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [config.type])

	useEffect(() => {
		load()
	}, [load])

	const add = () => {
		if (draft.name.trim().length < 2) return
		if (config.grouped && draft.groupName.trim().length < 2) {
			toast.warning('Bitte eine Gruppe angeben.')
			return
		}

		http.post(TApi.ADMIN_SUPPORTER_LOOKUP_LIST(config.type), {
			name: draft.name.trim(),
			groupName: config.grouped ? draft.groupName.trim() : undefined
		})
			.then(() => {
				setDraft({ name: '', groupName: config.grouped ? draft.groupName : '' })
				load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
	}

	const toggleActive = row => {
		http.put(TApi.ADMIN_SUPPORTER_LOOKUP_ITEM(config.type, row.id), {
			isActive: !row.isActive
		})
			.then(load)
			.catch(() => {}) // Meldung kommt aus shared/http
	}

	const remove = async row => {
		const inUse = row.usedBy > 0

		const result = await Swal.fire({
			title: row.name,
			text: inUse
				? `Dieser Eintrag ist ${row.usedBy}-mal zugeordnet. Er wird deshalb nur deaktiviert und verschwindet aus neuen Auswahllisten — in bereits gepflegten Profilen bleibt er sichtbar.`
				: 'Dieser Eintrag ist nirgends zugeordnet und wird endgültig entfernt.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: inUse ? 'Deaktivieren' : t('delete'),
			cancelButtonText: t('Cancel'),
			confirmButtonColor: inUse ? '#008b9c' : '#d33'
		})
		if (!result.isConfirmed) return

		http.delete(TApi.ADMIN_SUPPORTER_LOOKUP_ITEM(config.type, row.id))
			.then(load)
			.catch(() => {}) // Meldung kommt aus shared/http
	}

	// Kompetenzen nach Gruppe buendeln, alles andere als flache Liste.
	//
	// Deaktiviertes steht hinten — innerhalb der Gruppe und, wenn eine Gruppe gar
	// nichts Auswaehlbares mehr enthaelt, als ganze Gruppe. Sonst entscheidet die
	// Reihenfolge im Datensatz, und eine ausrangierte Gruppe schiebt sich zwischen
	// die gepflegten (auf Test stand „Betriebswirtschaft" mit zwei durchgestrichenen
	// Eintraegen ueber dem gesamten abgestimmten Katalog).
	const groups = config.grouped
		? [...new Set(rows.map(r => r.groupName))]
				.map(name => {
					const items = rows.filter(r => r.groupName === name)
					return {
						name,
						items: [...items].sort((a, b) => Number(b.isActive) - Number(a.isActive)),
						hasActive: items.some(r => r.isActive)
					}
				})
				.sort((a, b) => Number(b.hasActive) - Number(a.hasActive))
		: [{ name: null, items: rows }]

	return (
		<div className={shared.root} id={`supporter-${config.type}`}>
			<section className={shared.section}>
				<h3 className={shared.sectionTitle}>{config.title}</h3>
				<SettingHint example={config.example}>{config.description}</SettingHint>

				<div className={shared.sectionContent}>
					{groups.map(group => (
						<div key={group.name || 'flat'} className={styles.group}>
							{group.name && <span className={styles.groupName}>{group.name}</span>}
							<div className={styles.items}>
								{group.items.map(row => (
									<span
										key={row.id}
										className={clsx(styles.item, !row.isActive && styles.itemInactive)}
									>
										<span className={styles.itemName}>{row.name}</span>
										{row.usedBy > 0 && (
											<span className={styles.usage} title="Zugeordnet in Profilen">
												{row.usedBy}×
											</span>
										)}
										<button
											type="button"
											className={styles.toggle}
											onClick={() => toggleActive(row)}
											title={row.isActive ? 'Deaktivieren' : 'Wieder aktivieren'}
										>
											{row.isActive ? '◉' : '○'}
										</button>
										<button
											type="button"
											className={styles.remove}
											onClick={() => remove(row)}
											title={t('delete')}
										>
											×
										</button>
									</span>
								))}
								{!group.items.length && !isLoading && (
									<span className={styles.empty}>Noch keine Einträge.</span>
								)}
							</div>
						</div>
					))}
				</div>

				<div className={styles.addRow}>
					{config.grouped && (
						<PlainInput
							placeholder="Gruppe"
							value={draft.groupName}
							onChange={e => setDraft({ ...draft, groupName: e.target.value })}
						/>
					)}
					<PlainInput
						placeholder={config.grouped ? 'Kompetenz' : config.title.replace(/n$/, '')}
						value={draft.name}
						onChange={e => setDraft({ ...draft, name: e.target.value })}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								e.preventDefault()
								add()
							}
						}}
					/>
					<Btn type="button" onClick={add}>
						Hinzufügen
					</Btn>
				</div>
			</section>
		</div>
	)
}

export default function SupporterLookups({ form, updateOptions }) {
	return (
		<div id="supporterLookups">
			{LISTS.map(config => (
				<LookupList key={config.type} config={config} />
			))}

			<SupportCategories />

			<SupporterRequestReminders form={form} updateOptions={updateOptions} />

			<SupporterContactFollowUp form={form} updateOptions={updateOptions} />

			<SupporterApprovalReminders form={form} updateOptions={updateOptions} />

			<SupporterInvitationReminders form={form} updateOptions={updateOptions} />
		</div>
	)
}

SupporterLookups.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
